/**
 * The play document model and the pure operations over it.
 *
 * Every operation returns a new document; nothing mutates in place. Ids and
 * timestamps are supplied by the caller so that `core` stays free of globals and
 * so that write paths are sync-ready (stable ids, explicit clocks, no hidden
 * state).
 */

import { clampToField, clipPathToField, type Vec2 } from './geometry.ts';
import type { Id, IdFactory } from './ids.ts';
import type { FormationDef, SportPack } from './sport.ts';
import type { BoardView } from './view.ts';
import { UNTITLED_PLAY } from './plays.ts';

export const SCHEMA_VERSION = 3;

export type ShapeKind = 'move' | 'pass' | 'freehand' | 'shot';

export interface Entity {
  readonly id: Id;
  readonly typeId: string;
  readonly teamId?: string;
  readonly roleId?: string;
  readonly label: string;
  /**
   * The coach's own name for this player's position, shown instead of the
   * role's short form. The role itself stays, so the lineup, the goalie's cap
   * and colour coding keep working. Absent means the role's own name.
   */
  readonly roleLabel?: string;
}

export interface Shape {
  readonly id: Id;
  readonly kind: ShapeKind;
  readonly points: readonly Vec2[];
  readonly color: string;
  /** Stroke width in field units. */
  readonly width: number;
  /**
   * The phase this drawing belongs to. It is on screen for that phase and the
   * transition out of it, so an arrow explaining the next movement is still
   * there while the movement happens.
   */
  readonly keyframeId: Id;
  /**
   * The piece this stroke moves, when it was drawn from one: the player of a
   * swim, the ball of a pass or a shot. Such a stroke is a lane —
   * the route that piece takes out of this phase into the next.
   */
  readonly moverId?: Id;
  /** What travels along the lane beside the mover, e.g. the ball a swimmer has. */
  readonly carriedIds?: readonly Id[];
  /**
   * The player a pass or a shot is played from — the one holding the ball when
   * it was drawn. The pass waits for them to be free, so a player who swims to
   * the ball gets there before it leaves.
   */
  readonly fromId?: Id;
}

export interface Keyframe {
  readonly id: Id;
  /** entityId -> position in field space. */
  readonly positions: Readonly<Record<Id, Vec2>>;
}

export interface PlayDocument {
  readonly schemaVersion: number;
  readonly id: Id;
  readonly name: string;
  readonly sportPackId: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly entities: readonly Entity[];
  readonly shapes: readonly Shape[];
  /** In running order, always at least one frame. Timing is derived, not stored. */
  readonly keyframes: readonly Keyframe[];
  /**
   * The view the coach left the board in. A camera setting, not part of the
   * play: optional and additive, so it needs no schema bump, and absent means
   * the full field.
   */
  readonly view?: BoardView;
  /**
   * On the board: the saved play it came from, which Save updates (#92).
   * Absent on a board no saved play is behind, and on saved plays themselves.
   * Optional and additive, like `view`.
   */
  readonly playId?: Id;
}

/** Keeps entities clear of the very edge of the surface. */
export const FIELD_INSET = 0.012;

/** Used when a pack declares no roster rule to name its player type. */
const DEFAULT_PLAYER_TYPE = 'player';

function touch(doc: PlayDocument, now: number): PlayDocument {
  return { ...doc, updatedAt: now };
}

export interface CreateDocumentOptions {
  readonly pack: SportPack;
  readonly newId: IdFactory;
  readonly now: number;
  readonly name?: string;
  readonly formationId?: string;
}

/** Builds a fresh document from a sport pack's default formation. */
export function createDocument(options: CreateDocumentOptions): PlayDocument {
  const { pack, newId, now } = options;
  const formationId = options.formationId ?? pack.defaultFormationId;
  const formation = pack.formations.find((f) => f.id === formationId);
  if (!formation) throw new Error(`Unknown formation "${formationId}" in pack "${pack.id}"`);

  const { entities, positions } = buildFormationEntities(pack, formation, newId);

  return {
    schemaVersion: SCHEMA_VERSION,
    id: newId('play'),
    name: options.name ?? UNTITLED_PLAY,
    sportPackId: pack.id,
    createdAt: now,
    updatedAt: now,
    entities,
    shapes: [],
    keyframes: [{ id: newId('kf'), positions }],
  };
}

function buildFormationEntities(
  pack: SportPack,
  formation: FormationDef,
  newId: IdFactory,
): { entities: Entity[]; positions: Record<Id, Vec2> } {
  const playerTypeId = pack.roster?.playerTypeId ?? DEFAULT_PLAYER_TYPE;
  const entities: Entity[] = [];
  const positions: Record<Id, Vec2> = {};

  for (const slot of formation.slots) {
    const id = newId('e');
    entities.push({ id, typeId: playerTypeId, teamId: slot.teamId, roleId: slot.roleId, label: slot.label });
    positions[id] = clampToField(slot.at, FIELD_INSET);
  }
  for (const prop of formation.props) {
    const id = newId('e');
    entities.push({ id, typeId: prop.typeId, label: prop.label ?? '' });
    positions[id] = clampToField(prop.at, FIELD_INSET);
  }

  return { entities, positions };
}

/** Replaces the board with a formation from the pack, resetting the play to one phase. */
export function applyFormation(
  doc: PlayDocument,
  pack: SportPack,
  formation: FormationDef,
  newId: IdFactory,
  now: number,
): PlayDocument {
  const { entities, positions } = buildFormationEntities(pack, formation, newId);
  return {
    ...doc,
    entities,
    shapes: [],
    keyframes: [{ id: newId('kf'), positions }],
    updatedAt: now,
  };
}

export function findKeyframeIndex(doc: PlayDocument, keyframeId: Id): number {
  return doc.keyframes.findIndex((k) => k.id === keyframeId);
}

/** Position of an entity in a specific keyframe, falling back to the first frame. */
export function positionIn(doc: PlayDocument, keyframeId: Id, entityId: Id): Vec2 | undefined {
  const frame = doc.keyframes.find((k) => k.id === keyframeId) ?? doc.keyframes[0];
  return frame?.positions[entityId];
}

export interface EntityMove {
  readonly id: Id;
  readonly to: Vec2;
}

/**
 * Moves several entities within one keyframe as a single edit. Each is clamped
 * to the field on its own, so a piece that reaches the wall stops there while
 * the others keep going — a group drag never stalls as a whole.
 */
export function moveEntities(
  doc: PlayDocument,
  keyframeId: Id,
  moves: readonly EntityMove[],
  now: number,
): PlayDocument {
  const keyframes = doc.keyframes.map((frame) => {
    if (frame.id !== keyframeId) return frame;
    const positions = { ...frame.positions };
    for (const move of moves) positions[move.id] = clampToField(move.to, FIELD_INSET);
    return { ...frame, positions };
  });
  return touch({ ...doc, keyframes }, now);
}

/**
 * Moves an entity within one keyframe. This is the drag operation: dragging on
 * the board edits the keyframe the playhead is parked on, not a global position.
 */
export function moveEntity(doc: PlayDocument, keyframeId: Id, entityId: Id, to: Vec2, now: number): PlayDocument {
  return moveEntities(doc, keyframeId, [{ id: entityId, to }], now);
}

export function addEntity(
  doc: PlayDocument,
  entity: Entity,
  at: Vec2,
  now: number,
): PlayDocument {
  const clamped = clampToField(at, FIELD_INSET);
  const keyframes = doc.keyframes.map((frame) => ({
    ...frame,
    positions: { ...frame.positions, [entity.id]: clamped },
  }));
  return touch({ ...doc, entities: [...doc.entities, entity], keyframes }, now);
}

/** Gives a player a new number (or whatever label the coach writes). */
export function setEntityLabel(doc: PlayDocument, entityId: Id, label: string, now: number): PlayDocument {
  if (!doc.entities.some((entity) => entity.id === entityId)) return doc;
  return touch({ ...doc, entities: doc.entities.map((e) => (e.id === entityId ? { ...e, label } : e)) }, now);
}

/**
 * Renames a player's position for this board. An empty name puts the role's
 * own name back.
 */
export function setRoleLabel(doc: PlayDocument, entityId: Id, roleLabel: string, now: number): PlayDocument {
  if (!doc.entities.some((entity) => entity.id === entityId)) return doc;
  const text = roleLabel.trim();
  const entities = doc.entities.map((entity) => {
    if (entity.id !== entityId) return entity;
    const { roleLabel: _previous, ...rest } = entity;
    return text ? { ...rest, roleLabel: text } : rest;
  });
  return touch({ ...doc, entities }, now);
}

export function removeEntity(doc: PlayDocument, entityId: Id, now: number): PlayDocument {
  const keyframes = doc.keyframes.map((frame) => {
    const positions = { ...frame.positions };
    delete positions[entityId];
    return { ...frame, positions };
  });
  return touch(
    { ...doc, entities: doc.entities.filter((e) => e.id !== entityId), keyframes },
    now,
  );
}

/**
 * Keeps a shape's points to the field, except freehand.
 *
 * An arrow pointing into the surround is not a play, so a pass, move
 * or shot is cut where it leaves the field and keeps the direction it was
 * drawn with. Freehand is how a coach writes notes in the margin, so it may go
 * anywhere. An arrow that never touches the field comes back empty; the
 * caller drops it rather than keep a stub.
 */
export function confineShape(kind: ShapeKind, points: readonly Vec2[]): Vec2[] {
  return kind === 'freehand' ? [...points] : clipPathToField(points);
}

export function addShape(doc: PlayDocument, shape: Shape, now: number): PlayDocument {
  return touch({ ...doc, shapes: [...doc.shapes, shape] }, now);
}

export function removeShape(doc: PlayDocument, shapeId: Id, now: number): PlayDocument {
  return touch({ ...doc, shapes: doc.shapes.filter((s) => s.id !== shapeId) }, now);
}

/** Clears one phase's drawings; the rest of the play keeps its own. */
export function clearShapes(doc: PlayDocument, keyframeId: Id, now: number): PlayDocument {
  return touch({ ...doc, shapes: doc.shapes.filter((s) => s.keyframeId !== keyframeId) }, now);
}

/**
 * Copies a phase and puts the copy straight after it — "add a step here", not
 * only at the end. Without a source, the copy goes after the last phase.
 */
export function addKeyframe(
  doc: PlayDocument,
  newId: IdFactory,
  now: number,
  options: { readonly afterKeyframeId?: Id } = {},
): PlayDocument {
  const named = options.afterKeyframeId
    ? doc.keyframes.findIndex((k) => k.id === options.afterKeyframeId)
    : -1;
  // A phase that is not in the play is no anchor: fall back to the end rather
  // than let a missed lookup put the copy first.
  const index = named < 0 ? doc.keyframes.length - 1 : named;
  const source = doc.keyframes[index];
  if (!source) throw new Error('Document has no keyframes');

  const frame: Keyframe = { id: newId('kf'), positions: { ...source.positions } };
  const keyframes = [...doc.keyframes];
  keyframes.splice(index + 1, 0, frame);
  return touch({ ...doc, keyframes }, now);
}

/**
 * Puts a copy of the first phase in front of it, with none of its drawings.
 * A move drawn on the first phase needs somewhere for its pieces to start
 * from: the copy holds everyone where they stood, and the phase drawn on —
 * its id, and so its drawings, unchanged — becomes the second.
 */
export function addStartPhase(doc: PlayDocument, newId: IdFactory, now: number): PlayDocument {
  const first = doc.keyframes[0];
  if (!first) throw new Error('Document has no keyframes');
  const frame: Keyframe = { id: newId('kf'), positions: { ...first.positions } };
  return touch({ ...doc, keyframes: [frame, ...doc.keyframes] }, now);
}

/** Removes a phase and the drawings that belonged to it — one edit, one undo. */
export function removeKeyframe(doc: PlayDocument, keyframeId: Id, now: number): PlayDocument {
  if (doc.keyframes.length <= 1) return doc;
  return touch(
    {
      ...doc,
      keyframes: doc.keyframes.filter((k) => k.id !== keyframeId),
      shapes: doc.shapes.filter((s) => s.keyframeId !== keyframeId),
    },
    now,
  );
}

export function entityById(doc: PlayDocument, entityId: Id): Entity | undefined {
  return doc.entities.find((e) => e.id === entityId);
}
